import { Helpers } from './Helpers';
import { FARKLI_DOVIZ } from './dipToplam';
import { GelirToplami } from '../models/response/ambarVoyage/GetAmbarVoyageByIdResponse';

/**
 * Sefer Fiyat Bul (13.10.2026): sefer siparislerinin gelir toplamlari ve Fiyat Bul
 * penceresindeki "Bu sefere bagli diger siparisler" satirlari icin saf fonksiyonlar.
 */

export const BOS_HUCRE = '—';

/** tr-TR, 2 hane. correctPrice isareti atiyor; eksi toplam eksi gorunsun. */
export const paraMetni = (tutar: number) =>
    (tutar < 0 ? '-' : '') + Helpers.correctPrice(tutar, 2).trim();

const dovizAnahtari = (x: GelirToplami) => x.currencyTypeId ?? x.dovizKodu ?? '';

/** Birden cok siparisin toplamlarini doviz cinsine gore birlestirir (sefer toplami). */
export const gelirToplamlariniBirlestir = (listeler: (GelirToplami[] | null | undefined)[]): GelirToplami[] => {
    const sonuc: GelirToplami[] = [];

    listeler.forEach(liste => (liste ?? []).forEach(x => {
        const var_ = sonuc.find(s => dovizAnahtari(s) == dovizAnahtari(x));

        if (var_) {
            var_.net += Number(x.net) || 0;
            var_.brut += Number(x.brut) || 0;
            var_.dovizKodu = var_.dovizKodu ?? x.dovizKodu;
        }
        else
            sonuc.push({ currencyTypeId: x.currencyTypeId, dovizKodu: x.dovizKodu, net: Number(x.net) || 0, brut: Number(x.brut) || 0 });
    }));

    return sonuc;
}

/** Hucre metni: bossa "—", tek dovizse "1.234,50 TRY", birden cok dovizse "Farkli Doviz Cinsi". */
export const gelirToplamiMetni = (toplamlar: GelirToplami[] | null | undefined, alan: 'net' | 'brut'): string => {
    const birlesik = gelirToplamlariniBirlestir([toplamlar]);

    if (birlesik.length == 0)
        return BOS_HUCRE;

    if (birlesik.length > 1)
        return FARKLI_DOVIZ;

    return [paraMetni(birlesik[0][alan]), birlesik[0].dovizKodu].filter(x => x).join(' ');
}

/** Beklenen gelir satiri aciklamasina eklenen sefer izi: "Sefer hesabı · {RefNo} · {tarih}". */
export const seferHesabiNotu = (satir: { seferHesabiRefNo?: string | null, seferHesabiTarihiStr?: string | null }) =>
    satir.seferHesabiRefNo
        ? ['Sefer hesabı', satir.seferHesabiRefNo, satir.seferHesabiTarihiStr].filter(x => x).join(' · ')
        : '';

/** Aciklama + sefer izi tek metin. */
export const aciklamaVeSeferNotu = (satir: { description?: string | null, seferHesabiRefNo?: string | null, seferHesabiTarihiStr?: string | null }) =>
    [satir.description, seferHesabiNotu(satir)].filter(x => x).join(' · ');

interface UyeGelirSatiri {
    agreementSchemeId?: string | null
    serviceName?: any
    currencyTypeName?: any
    totalWithoutTaxPrice?: number | string | null
    totalPrice?: number | string | null
    unitPrice?: number | string | null
    quantity?: number | string | null
    /** Toplama - Dagitim KM zinciri (14.10.2026); zincir disinda null. */
    seferKm?: number | null
    seferKmBirimFiyat?: number | null
    seferKmAciklama?: string | null
    /** Yalniz dayanak (en uzak nokta) satirinda: anlasma fiyati ve yukleme → teslim KM'si (15.10.2026). */
    seferKmDayanakFiyat?: number | null
    seferKmDayanakKm?: number | null
}

interface Uye {
    ambarId: string
    refNo: string
    seferdenEklendi?: boolean
    fiyatBulAtlamaNedeni?: string | null
    ambarVoyageRefNo?: string | null
    ambarVoyageOrder?: number | null
    isAdditionalLocation?: boolean
    ambarExpectedIncomeOutcomes?: UyeGelirSatiri[]
}

export type SeferSonucTipi = 'yazilacak' | 'atlandi' | 'bulunamadi';

export interface SeferUyeSatiri {
    anahtar: string
    seferRefNo: string
    sira: string
    refNo: string
    ekLokasyon: 'Evet' | 'Hayır'
    hizmet: string
    toplam: string
    brut: string
    sonuc: string
    sonucTipi: SeferSonucTipi
    /** KM zinciri satirinda sunucunun KM aciklamasi; degilse bos. */
    kmAciklama: string
}

export const SONUC_YAZILACAK = 'Yazılacak';
export const SONUC_BULUNAMADI = 'Fiyat bulunamadı';

const tutarMetni = (tutar: number | string | null | undefined, doviz: any) =>
    [paraMetni(Number(tutar) || 0), doviz].filter(x => x).join(' ');

/**
 * Sefer uyeleri tablosu: her fiyat satiri bir tablo satiri; fiyati olmayan ya da atlanan uye tek satir.
 * Siralama: sefer, sonra seferdeki sira. Yalniz sablon satirlari (agreementSchemeId dolu) yazilir.
 */
export const seferUyeSatirlari = (uyeler: Uye[]): SeferUyeSatiri[] => {
    const sirali = [...uyeler].sort((a, b) =>
        (a.ambarVoyageRefNo ?? '').localeCompare(b.ambarVoyageRefNo ?? '')
        || (a.ambarVoyageOrder ?? 0) - (b.ambarVoyageOrder ?? 0));

    return sirali.flatMap(uye => {
        const ortak = {
            seferRefNo: uye.ambarVoyageRefNo ?? '',
            // Sefere siparis ekranindan baglanan sipariste sira kaydedilmiyor (0); o zaman bos hucre.
            sira: uye.ambarVoyageOrder ? String(uye.ambarVoyageOrder) : BOS_HUCRE,
            refNo: uye.refNo,
            ekLokasyon: (uye.isAdditionalLocation ? 'Evet' : 'Hayır') as 'Evet' | 'Hayır'
        };

        const satirlar = (uye.ambarExpectedIncomeOutcomes ?? []).filter(x => x.agreementSchemeId);

        if (uye.fiyatBulAtlamaNedeni || satirlar.length == 0)
            return [{
                ...ortak,
                anahtar: uye.ambarId,
                hizmet: BOS_HUCRE,
                toplam: BOS_HUCRE,
                brut: BOS_HUCRE,
                sonuc: uye.fiyatBulAtlamaNedeni || SONUC_BULUNAMADI,
                sonucTipi: (uye.fiyatBulAtlamaNedeni ? 'atlandi' : 'bulunamadi') as SeferSonucTipi,
                kmAciklama: ''
            }];

        return satirlar.map((x, i) => ({
            ...ortak,
            anahtar: uye.ambarId + '-' + (x.agreementSchemeId ?? i),
            hizmet: x.serviceName ?? '',
            toplam: tutarMetni(x.totalWithoutTaxPrice, x.currencyTypeName),
            brut: tutarMetni(x.totalPrice, x.currencyTypeName),
            sonuc: SONUC_YAZILACAK,
            sonucTipi: 'yazilacak' as SeferSonucTipi,
            kmAciklama: x.seferKmAciklama ?? ''
        }));
    });
}

/** Kaydet yanitinda seferden eklenip gercekten yazilan siparis sayisi (basari mesaji icin). */
export const seferdeYazilanSayisi = (yanit: Uye[], tiklananAmbarId?: string) =>
    yanit.filter(x => x.seferdenEklendi
        && x.ambarId != tiklananAmbarId
        && !x.fiyatBulAtlamaNedeni
        && (x.ambarExpectedIncomeOutcomes ?? []).some(s => s.agreementSchemeId)).length;

/** Toplu ekranda ayri listede gosterilecekler: seferden eklenenler ve atlananlar. */
export const seferListesindeMi = (x: Uye) => !!x.seferdenEklendi || !!x.fiyatBulAtlamaNedeni;

/*
 * Yurtici Tasima (Toplama - Dagitim) KM fiyati (14.10.2026, 15.10.2026 guncel): yalniz ek lokasyonsuz siparis
 * (en uzak nokta, dayanak) anlasmadan fiyat alir; KM birim fiyati = dayanak fiyati / dayanagin yukleme → teslim KM'si.
 * Siradaki her sipariste (dayanak dahil) Miktar = KM, Birim Fiyat = KM birim fiyati.
 * Degerleri sunucu hesaplar; burada yalniz gosterim.
 */

const sayiMi = (x: number | string | null | undefined): x is number | string =>
    x !== null && x !== undefined && x !== '' && !isNaN(Number(x));

/** "1.150 km" (tam sayi, tr-TR binlik); bossa "—". */
export const kmMetni = (km: number | string | null | undefined) =>
    sayiMi(km) ? Math.round(Number(km)).toLocaleString('tr-TR') + ' km' : BOS_HUCRE;

/** KM birim fiyati 4 hane ("1,0000"), doviz verilirse sonuna; bossa "—". */
export const kmBirimMetni = (birim: number | string | null | undefined, doviz?: string | null) =>
    sayiMi(birim) ? [Helpers.correctPrice(birim, 4).trim(), doviz].filter(x => x).join(' ') : BOS_HUCRE;

/** Sefer toplami KM: dolu KM'lerin toplami; hicbiri dolu degilse null. */
export const seferKmToplami = (kmler: (number | string | null | undefined)[]): number | null => {
    const dolu = kmler.filter(sayiMi).map(x => Math.round(Number(x)));
    return dolu.length > 0 ? dolu.reduce((a, b) => a + b, 0) : null;
}

/** Tek doviz cinsiyse kodu (KM birim fiyati hucresine eklenir); bos ya da birden cok dovizde undefined. */
export const tekDovizKodu = (toplamlar: GelirToplami[] | null | undefined) => {
    const birlesik = gelirToplamlariniBirlestir([toplamlar]);
    return birlesik.length == 1 ? birlesik[0].dovizKodu ?? undefined : undefined;
}

/** Aciklama hucresinin altinda gosterilecek KM aciklamasi; aciklama metni zaten iceriyorsa tekrar yazilmaz. */
export const kmAciklamasiGoster = (satir: { description?: string | null, seferKmAciklama?: string | null }) => {
    const km = (satir.seferKmAciklama ?? '').trim();
    return km && !(satir.description ?? '').includes(km) ? km : '';
}

/** Birim Fiyat = KM birim fiyati olan satir (zincirdeki KM satiri): birim fiyat 4 hane gosterilir. */
export const kmBirimSatiriMi = (satir: { unitPrice?: number | string | null, seferKmBirimFiyat?: number | null }) => {
    const birim = satir.unitPrice === null || satir.unitPrice === undefined || satir.unitPrice === ''
        ? NaN
        : Number(String(satir.unitPrice).replace(',', '.'));

    return sayiMi(satir.seferKmBirimFiyat) && !isNaN(birim) && Math.abs(birim - Number(satir.seferKmBirimFiyat)) < 0.00005;
}

export const KM_BIRIM_ISARETI = 'KM birim fiyatı';

export interface KmBirimBilgisi {
    /** Dayanak (en uzak nokta) siparisin numarasi. */
    refNo: string
    /** Dayanagin anlasma fiyati. */
    fiyat: number
    /** Dayanagin yukleme → teslim KM'si. */
    km1: number
    kmBirim: number
    doviz: string
}

/**
 * Pencerenin ustundeki bilgi kutusu: dayanagin satiri (KM aciklamasinda "KM birim fiyatı" geciyor; yoksa Miktar = 1
 * olan KM satiri). Fiyat ve KM sunucunun gonderdigi dayanak alanlarindan; gelmezse (eski yanit) satirdan. Bulunamazsa null.
 */
export const kmBirimBilgisi = (siparisler: { refNo: string, ambarExpectedIncomeOutcomes?: UyeGelirSatiri[] | null }[]): KmBirimBilgisi | null => {
    const adaylar = siparisler.flatMap(s => (s.ambarExpectedIncomeOutcomes ?? [])
        .filter(x => x.agreementSchemeId && sayiMi(x.seferKm) && sayiMi(x.seferKmBirimFiyat))
        .map(x => ({ refNo: s.refNo, x })));

    const ilk = adaylar.find(a => (a.x.seferKmAciklama ?? '').includes(KM_BIRIM_ISARETI))
        ?? adaylar.find(a => Number(a.x.quantity) == 1);

    if (!ilk)
        return null;

    const miktar = sayiMi(ilk.x.quantity) ? Number(ilk.x.quantity) : 1;
    const satirFiyati = sayiMi(ilk.x.unitPrice) ? Number(ilk.x.unitPrice) * miktar : Number(ilk.x.totalWithoutTaxPrice) || 0;

    return {
        refNo: ilk.refNo,
        fiyat: sayiMi(ilk.x.seferKmDayanakFiyat) ? Number(ilk.x.seferKmDayanakFiyat) : satirFiyati,
        km1: sayiMi(ilk.x.seferKmDayanakKm) ? Number(ilk.x.seferKmDayanakKm) : Number(ilk.x.seferKm),
        kmBirim: Number(ilk.x.seferKmBirimFiyat),
        doviz: ilk.x.currencyTypeName || 'TRY'
    };
}

/** "KM birim fiyatı: 1.000,00 ÷ 1.000 km = 1,0000 TRY/km" */
export const kmBirimBilgisiMetni = (b: KmBirimBilgisi) =>
    KM_BIRIM_ISARETI + ': ' + paraMetni(b.fiyat) + ' ÷ ' + kmMetni(b.km1) + ' = ' + kmBirimMetni(b.kmBirim) + ' ' + b.doviz + '/km';
